import React, { useMemo } from "react";
import { MapContainer, TileLayer, CircleMarker, Tooltip, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { MAP_LOCS, GOLD, tf, ts } from "./data";

// Static style/layout constants — extracted to avoid creating new objects per render.
const ROUTE_STYLE = { color: GOLD, weight: 2.2, opacity: 0.85, dashArray: "6 6" };
const MAP_STYLE = { height: "100%", width: "100%", background: "#0b0b18" };
const CONTAINER_STYLE = {
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "20px",
  overflow: "hidden",
  height: "560px",
};
const FIT_PADDING = [40, 40];
const CENTER = [35.5, 136];

// Pre-compute per-marker pathOptions once (color is static per location).
const MARKER_OPTIONS = MAP_LOCS.reduce((acc, loc) => {
  acc[loc.name] = {
    color: "#ffffff",
    weight: 2,
    fillColor: loc.color,
    fillOpacity: 0.95,
  };
  return acc;
}, {});

function FitBounds({ points }) {
  const map = useMap();
  React.useEffect(() => {
    if (!points.length) return;
    map.fitBounds(points, { padding: FIT_PADDING });
  }, [map, points]);
  return null;
}

const TOOLTIP_INNER_STYLE = { minWidth: 140 };
const TOOLTIP_EMOJI_STYLE = { fontSize: "1.1rem", marginBottom: 2 };
const TOOLTIP_NAME_STYLE = { ...ts, color: "#fff", fontSize: "0.95rem" };
const TOOLTIP_META_STYLE = { ...tf, color: GOLD, fontSize: "0.7rem" };
const TOOLTIP_SUB_STYLE = { ...tf, color: "rgba(255,255,255,0.55)", fontSize: "0.66rem" };
const TOOLTIP_OFFSET = [0, -10];

export default function JapanMap() {
  const points = useMemo(() => MAP_LOCS.map((l) => l.coords), []);
  const route = useMemo(
    () => [...MAP_LOCS].sort((a, b) => a.order - b.order).map((l) => l.coords),
    []
  );

  return (
    <div data-testid="japan-map-container" style={CONTAINER_STYLE}>
      <MapContainer
        center={CENTER}
        zoom={6}
        scrollWheelZoom={false}
        style={MAP_STYLE}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />
        <FitBounds points={points} />

        <Polyline positions={route} pathOptions={ROUTE_STYLE} />

        {MAP_LOCS.map((loc) => (
          <CircleMarker
            key={loc.name}
            center={loc.coords}
            radius={9}
            pathOptions={MARKER_OPTIONS[loc.name]}
          >
            <Tooltip direction="top" offset={TOOLTIP_OFFSET} opacity={1} className="japan-leaflet-tip">
              <div style={TOOLTIP_INNER_STYLE}>
                <div style={TOOLTIP_EMOJI_STYLE}>{loc.emoji}</div>
                <div style={TOOLTIP_NAME_STYLE}>{loc.name}</div>
                <div style={TOOLTIP_META_STYLE}>{loc.days}</div>
                <div style={TOOLTIP_SUB_STYLE}>
                  {loc.nights} {loc.hotel !== "—" ? `· ${loc.hotel}` : ""}
                </div>
              </div>
            </Tooltip>
          </CircleMarker>
        ))}
      </MapContainer>
    </div>
  );
}
